import { useEffect, useRef } from 'react';

const areas = [
  {
    id: 'penal',
    title: 'Direito Penal',
    subtitle: 'Defesa técnica e estratégica',
    icon: 'ri-scales-3-line',
    description:
      'Atuação especializada na defesa de clientes em processos criminais, com estratégia técnica, sigilo absoluto e acompanhamento integral em todas as fases do processo.',
    services: [
      'Defesa em processos criminais e inquéritos',
      'Habeas corpus e recursos criminais',
      'Acompanhamento em audiências e julgamentos',
      'Liberdade provisória e revogação de prisão',
      'Crimes contra o patrimônio e vida',
      'Crimes financeiros e tributários',
    ],
    image: 'https://readdy.ai/api/search-image?query=elegant%20dark%20courtroom%20with%20wooden%20benches%20and%20dramatic%20lighting%2C%20scales%20of%20justice%20symbol%20carved%20in%20dark%20marble%2C%20sophisticated%20legal%20atmosphere%2C%20deep%20shadows%20with%20warm%20golden%20accent%20lights%20illuminating%20the%20justice%20symbols%2C%20minimalist%20premium%20legal%20environment%2C%20no%20people%2C%20architectural%20shot&width=700&height=600&seq=penal01&orientation=landscape',
  },
  {
    id: 'previdenciario',
    title: 'Direito Previdenciário',
    subtitle: 'Seus direitos garantidos',
    icon: 'ri-shield-star-line',
    description:
      'Assessoria completa em matéria previdenciária, desde o planejamento da aposentadoria até a defesa em recursos junto ao INSS e Justiça Federal, com agilidade e clareza.',
    services: [
      'Concessão e revisão de aposentadorias',
      'Benefício por incapacidade (auxílio-doença)',
      'Pensão por morte e benefícios dependentes',
      'Planejamento previdenciário estratégico',
      'Recursos administrativos e judiciais',
      'Reconhecimento de atividade especial',
    ],
    image: 'https://readdy.ai/api/search-image?query=professional%20legal%20documents%20and%20official%20papers%20on%20an%20elegant%20dark%20wooden%20desk%2C%20golden%20pen%20and%20official%20stamps%2C%20warm%20sophisticated%20lighting%2C%20minimalist%20composition%20with%20depth%2C%20premium%20legal%20office%20aesthetic%2C%20warm%20amber%20tones%20against%20dark%20wood%20textures%2C%20clean%20and%20prestigious%20atmosphere&width=700&height=600&seq=prev01&orientation=landscape',
  },
];

export default function PracticeAreas() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-area').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 200);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="areas"
      ref={sectionRef}
      className="py-28 lg:py-36 relative overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #0F0F0F 0%, #0D1B2A 60%, #0F0F0F 100%)' }}
    >
      {/* Pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `repeating-linear-gradient(-45deg, transparent, transparent 60px, #C8A24A 60px, #C8A24A 61px)`,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div
          className="fade-area text-center mb-20"
          style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-gold" />
            <span className="text-gold font-sans font-600 text-xs tracking-[0.25em] uppercase">Especialidades</span>
            <span className="w-10 h-px bg-gold" />
          </div>
          <h2 className="font-serif text-ice text-4xl lg:text-5xl font-700 mb-5">
            Áreas de Atuação
          </h2>
          <p className="font-sans text-ice/50 text-base font-300 max-w-xl mx-auto leading-relaxed">
            Atuamos com profundidade técnica e estratégia personalizada nas áreas em que somos verdadeiramente especializados.
          </p>
        </div>

        {/* Cards — carousel on mobile, grid on desktop */}
        <div
          className="-mx-6 px-[6vw] lg:mx-0 lg:px-0 flex overflow-x-auto snap-x snap-mandatory gap-5 pb-6 lg:grid lg:grid-cols-2 lg:gap-8 lg:overflow-visible lg:pb-0"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' } as React.CSSProperties}
        >
          {areas.map((area, i) => (
            <article
              key={area.id}
              className="fade-area group flex-none w-[88vw] snap-center lg:w-auto lg:flex-auto border border-gold/20 hover:border-gold/50 rounded-lg overflow-hidden transition-all duration-500 lg:last:mr-0"
              style={{
                opacity: 0,
                transform: 'translateY(30px)',
                transition: `opacity 0.8s ease ${i * 0.25}s, transform 0.8s ease ${i * 0.25}s, border-color 0.3s ease`,
                background: 'rgba(13,27,42,0.5)',
                backdropFilter: 'blur(10px)',
              }}
            >
              {/* Image */}
              <div className="relative h-52 overflow-hidden">
                <img
                  src={area.image}
                  alt={area.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/95 via-navy/40 to-transparent" />
                {/* Icon */}
                <div className="absolute bottom-5 left-6 w-12 h-12 flex items-center justify-center border border-gold/50 rounded-md bg-deep/60">
                  <i className={`${area.icon} text-gold text-2xl`} />
                </div>
              </div>

              {/* Content */}
              <div className="p-8 lg:p-10">
                <p className="font-sans text-gold text-xs font-600 tracking-[0.2em] uppercase mb-2">{area.subtitle}</p>
                <h3 className="font-serif text-ice text-2xl lg:text-3xl font-700 mb-4">{area.title}</h3>
                <p className="font-sans text-ice/60 text-sm font-300 leading-loose mb-7">{area.description}</p>

                {/* Services */}
                <ul className="grid grid-cols-1 gap-3">
                  {area.services.map((service) => (
                    <li key={service} className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                      <span className="font-sans text-ice/70 text-sm font-300">{service}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <a
                  href="https://wa.me/5598981578332?text=Olá,%20gostaria%20de%20consultar%20sobre%20" 
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-8 text-gold hover:text-gold-light font-sans text-sm font-500 transition-colors duration-300 cursor-pointer"
                >
                  Consultar sobre {area.title}
                  <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
